// src/pages/WHSComplianceRegister.tsx — G-04: Legislative Compliance Register
// Tracks PCBU duties and code of practice compliance. Government audit ready.
// WHS Act 2011 (Qld), WHS Regulation 2011 (Qld), Electrical Safety Act 2002 (Qld)

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_COMPLIANCE_REGISTER, LegislativeComplianceItem, ComplianceItemStatus, getWHSDocumentStyle } from '../utils/mockDataWHSMgmt'
import { Shield, CheckCircle, AlertTriangle, Clock, Download, X, ChevronRight, Sparkles, Search, Filter } from 'lucide-react'

const fmtDate = (s?: string|null) => s ? new Date(s).toLocaleDateString('en-AU',{day:'numeric',month:'short',year:'2-digit'}) : '—'

const STATUS_META: Record<ComplianceItemStatus, { label: string; bg: string; text: string; border: string; dot: string }> = {
  COMPLIANT:     { label: 'Compliant',      bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200',  dot: 'bg-green-500' },
  PARTIAL:       { label: 'Partial',        bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',  dot: 'bg-amber-500' },
  NON_COMPLIANT: { label: 'Non-compliant',  bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    dot: 'bg-red-500' },
  NOT_YET_DUE:   { label: 'Not yet due',    bg: 'bg-gray-50',   text: 'text-gray-500',   border: 'border-gray-200',   dot: 'bg-gray-300' },
  N_A:           { label: 'N/A',            bg: 'bg-gray-50',   text: 'text-gray-400',   border: 'border-gray-100',   dot: 'bg-gray-200' },
}

function ComplianceDetail({ item, onClose, canEdit }: { item: LegislativeComplianceItem; onClose: ()=>void; canEdit: boolean }) {
  const [editNotes, setEditNotes] = useState(item.notes ?? '')
  const [editEvidence, setEditEvidence] = useState(item.evidence)
  const [editStatus, setEditStatus] = useState(item.status)
  const [saved, setSaved] = useState(false)
  const sm = STATUS_META[item.status]

  const handleSave = () => { setSaved(true); setTimeout(() => setSaved(false), 2000) }

  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b bg-gray-50 flex-shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
              <span className="text-xs font-mono text-gray-400">{item.id.toUpperCase()}</span>
            </div>
            <div className="font-semibold text-gray-900 text-sm">{item.duty}</div>
            <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><Shield className="w-3 h-3"/>{item.legislation}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400"/></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-xs text-gray-500 mb-0.5">Frequency</div><div className="font-medium text-gray-900">{item.frequency}</div></div>
          <div><div className="text-xs text-gray-500 mb-0.5">Last checked</div><div className="font-medium">{fmtDate(item.lastChecked)}</div></div>
          <div><div className="text-xs text-gray-500 mb-0.5">Next due</div><div className={`font-medium ${item.nextDue && new Date(item.nextDue) < new Date() ? 'text-red-600' : 'text-gray-900'}`}>{fmtDate(item.nextDue)}</div></div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Status</div>
            {canEdit
              ? <select className="input text-sm" value={editStatus} onChange={e=>setEditStatus(e.target.value as ComplianceItemStatus)}>{(Object.keys(STATUS_META) as ComplianceItemStatus[]).map(s=><option key={s} value={s}>{STATUS_META[s].label}</option>)}</select>
              : <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">Evidence / compliance record {canEdit && <span className="text-gray-400">(editable)</span>}</div>
          {canEdit
            ? <textarea className="input text-sm w-full h-20 resize-none" value={editEvidence} onChange={e=>setEditEvidence(e.target.value)} placeholder="Document the evidence of compliance…"/>
            : <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">{item.evidence || <span className="text-gray-400 italic">No evidence recorded</span>}</div>}
        </div>
        {(item.notes || canEdit) && (
          <div>
            <div className="text-xs text-gray-500 mb-1">Notes / action required</div>
            {canEdit
              ? <textarea className="input text-sm w-full h-16 resize-none" value={editNotes} onChange={e=>setEditNotes(e.target.value)} placeholder="Any notes or required actions…"/>
              : item.notes && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">{item.notes}</div>}
          </div>
        )}
        {item.status === 'NON_COMPLIANT' && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-xs text-red-800 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"/>
            <div>This compliance item is <strong>non-compliant</strong>. A corrective action must be raised immediately. Use the Incidents module → Corrective Actions tab to create a CAR linked to this compliance obligation.</div>
          </div>
        )}
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800 flex items-start gap-2">
          <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5 text-blue-600"/>
          WHS AI Agent (planned) will monitor this obligation and alert 14 days before the next due date, cross-referencing evidence records to flag compliance drift.
        </div>
      </div>
      {canEdit && (
        <div className="border-t px-5 py-3 flex gap-2 flex-shrink-0">
          <button onClick={handleSave} className="btn-primary text-xs flex items-center gap-1">{saved ? <><CheckCircle className="w-3.5 h-3.5"/>Saved</> : 'Save changes'}</button>
          <button className="btn-secondary text-xs flex items-center gap-1"><Download className="w-3.5 h-3.5"/>Export evidence record</button>
        </div>
      )}
    </div>
  )
}

export function WHSComplianceRegister() {
  const { isTier } = useAuthStore()
  const canEdit = isTier('B_PC') || isTier('B_PM') || isTier('A_DEVELOPER')
  const [items, setItems] = useState(MOCK_COMPLIANCE_REGISTER)
  const [selected, setSelected] = useState<LegislativeComplianceItem|null>(null)
  const [filter, setFilter] = useState<ComplianceItemStatus|'ALL'>('ALL')
  const [search, setSearch] = useState('')
  const style = getWHSDocumentStyle()

  const filtered = items.filter(i => (filter==='ALL'||i.status===filter) && (!search||i.duty.toLowerCase().includes(search.toLowerCase())||i.legislation.toLowerCase().includes(search.toLowerCase())))

  const nc   = items.filter(i=>i.status==='NON_COMPLIANT').length
  const part = items.filter(i=>i.status==='PARTIAL').length
  const comp = items.filter(i=>i.status==='COMPLIANT').length

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Shield className="w-6 h-6 text-[#2E75B6]"/>Legislative Compliance Register</h1>
          <p className="text-sm text-gray-500 mt-0.5">PCBU duties · WHS Act 2011 · WHS Reg 2011 · Electrical Safety Act 2002 · EPA 1994 (Qld) · Government audit ready</p>
        </div>
        <button className="btn-secondary text-sm flex items-center gap-2"><Download className="w-4 h-4"/>Export for audit</button>
      </div>

      {nc > 0 && <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-800"><AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0"/><span><strong>{nc} non-compliant item{nc!==1?'s':''}</strong> require immediate corrective action. These represent active regulatory exposure.</span></div>}
      {part > 0 && <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-800"><Clock className="w-4 h-4 text-amber-600 flex-shrink-0"/><span><strong>{part} item{part!==1?'s':''} partially compliant.</strong> Complete the evidence records and confirm compliance before next audit.</span></div>}

      <div className="grid grid-cols-4 gap-3">
        {[
          { label:'Fully compliant', value:comp, color:'text-green-700', bg:'bg-green-50 border-green-200' },
          { label:'Partial', value:part, color:part>0?'text-amber-700':'text-gray-400', bg:part>0?'bg-amber-50 border-amber-200':'bg-white border-gray-200' },
          { label:'Non-compliant', value:nc, color:nc>0?'text-red-600':'text-gray-400', bg:nc>0?'bg-red-50 border-red-200':'bg-white border-gray-200' },
          { label:'Total obligations', value:items.length, color:'text-gray-900', bg:'bg-white border-gray-200' },
        ].map(({label,value,color,bg})=>(
          <div key={label} className={`border rounded-xl px-4 py-3 ${bg}`}>
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-48"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"/><input className="input pl-9 text-sm" placeholder="Search duties or legislation…" value={search} onChange={e=>setSearch(e.target.value)}/></div>
        {(['ALL','NON_COMPLIANT','PARTIAL','COMPLIANT','NOT_YET_DUE'] as (ComplianceItemStatus|'ALL')[]).map(f=>{
          const count = f==='ALL'?items.length:items.filter(i=>i.status===f).length
          if(count===0&&f!=='ALL') return null
          return <button key={f} onClick={()=>setFilter(f)} className={`text-xs px-3 py-1.5 rounded-full font-medium ${filter===f?'bg-[#1F3864] text-white':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{f==='ALL'?'All':STATUS_META[f].label} <span className="opacity-60">{count}</span></button>
        })}
      </div>

      <div className="flex gap-5 items-start">
        <div className={`${selected?'w-1/2':'w-full'} space-y-1 transition-all`}>
          {filtered.map(item => {
            const sm = STATUS_META[item.status]; const isSel = selected?.id===item.id
            const isOverdue = item.nextDue && new Date(item.nextDue) < new Date() && item.status !== 'COMPLIANT'
            return (
              <div key={item.id} onClick={()=>setSelected(isSel?null:item)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${isSel?'border-blue-400 ring-2 ring-blue-100':item.status==='NON_COMPLIANT'?'border-red-300':item.status==='PARTIAL'?'border-amber-300':isOverdue?'border-orange-200':'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${sm.dot}`}/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${sm.bg} ${sm.text} ${sm.border}`}>{sm.label}</span>
                      <span className="text-xs text-gray-500 font-mono">{item.legislation}</span>
                      {isOverdue && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold">OVERDUE</span>}
                    </div>
                    <div className="font-medium text-gray-900 text-sm">{item.duty}</div>
                    <div className="text-xs text-gray-400 mt-0.5 flex gap-3"><span>{item.frequency}</span>{item.nextDue&&<span>Next: {fmtDate(item.nextDue)}</span>}</div>
                    {item.notes && item.status !== 'COMPLIANT' && <div className="text-xs text-red-700 mt-1 bg-red-50 rounded px-2 py-1">{item.notes}</div>}
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSel?'text-blue-500':'text-gray-400'}`}/>
                </div>
              </div>
            )
          })}
        </div>
        {selected && (
          <div className="w-1/2 sticky top-6" style={{maxHeight:'calc(100vh - 160px)'}}>
            <ComplianceDetail item={selected} onClose={()=>setSelected(null)} canEdit={canEdit}/>
          </div>
        )}
      </div>
    </div>
  )
}
